export function answerMetric(caseResult, key) {
  const metrics = caseResult?.answer_metrics;
  if (!metrics || metrics[key] === undefined || metrics[key] === null || metrics[key] === "") {
    return null;
  }
  return metrics[key];
}

export function hasAnswerMetrics(caseResult) {
  return [
    "quality_score",
    "answer_relevancy",
    "faithfulness",
    "context_precision",
    "context_recall",
  ].some((key) => answerMetric(caseResult, key) !== null);
}

export function answerEvaluationMethod(caseResult) {
  const method = caseResult?.answer_metrics?.evaluation_method;
  return method || "";
}

export function answerEvaluationMethodLabel(method) {
  return (
    {
      deterministic_baseline: "Базовая проверка",
      llm_judge: "LLM-судья",
    }[method] ||
    method ||
    "-"
  );
}

export function canDownloadRagasExport(run) {
  return run?.status === "completed" && Number(run?.case_count || 0) > 0;
}

export function evaluationDocumentScopeLabel(run) {
  const count = Array.isArray(run?.document_ids) ? run.document_ids.length : 0;
  return count > 0 ? `${count} док.` : "Все";
}

export function evaluationRunDocumentIds(run) {
  return Array.isArray(run?.document_ids) ? [...run.document_ids] : [];
}

export function filterEvaluationRuns(runs, filters = {}) {
  const status = String(filters.status || "all");
  const profile = String(filters.profile || "all");
  return (runs || []).filter((run) => {
    if (status !== "all" && run?.status !== status) {
      return false;
    }
    if (profile !== "all" && run?.retrieval_profile !== profile) {
      return false;
    }
    return true;
  });
}

export function groupEvaluationRuns(runs) {
  const groups = [];
  const groupsByKey = new Map();

  for (const run of runs || []) {
    const datasetName = String(run?.dataset_name || "Без набора");
    const datasetVersion = String(run?.dataset_version || "без версии");
    const key = `${datasetName}\u0000${datasetVersion}`;
    let group = groupsByKey.get(key);
    if (!group) {
      group = {
        key,
        dataset_name: datasetName,
        dataset_version: datasetVersion,
        label: `${datasetName} ${datasetVersion}`,
        runs: [],
      };
      groupsByKey.set(key, group);
      groups.push(group);
    }
    group.runs.push(run);
  }

  return groups;
}

export function ragasExportEndpoint(runId) {
  return `/api/evaluation/runs/${encodeURIComponent(runId)}/ragas-export`;
}

export function communitySmokeEndpoint() {
  return "/api/evaluation/runs/community-smoke";
}

export function evaluationDatasetsEndpoint() {
  return "/api/evaluation/datasets";
}

export function evaluationDatasetEndpoint(datasetId) {
  return `/api/evaluation/datasets/${encodeURIComponent(datasetId)}`;
}

export function evaluationDatasetCasesEndpoint(datasetId) {
  return `/api/evaluation/datasets/${encodeURIComponent(datasetId)}/cases`;
}

export function evaluationDatasetCaseEndpoint(datasetId, caseId) {
  return `/api/evaluation/datasets/${encodeURIComponent(datasetId)}/cases/${encodeURIComponent(caseId)}`;
}

export function formatExpectedSourcesJson(sources) {
  const values = Array.isArray(sources) ? sources : [];
  return values.length > 0 ? JSON.stringify(values, null, 2) : "";
}

export function parseExpectedSourcesJson(value) {
  const text = String(value || "").trim();
  if (!text) {
    return [];
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (_error) {
    throw new Error("Ожидаемые источники должны быть корректным JSON-массивом");
  }
  if (!Array.isArray(parsed)) {
    throw new Error("Ожидаемые источники должны быть корректным JSON-массивом");
  }
  return parsed;
}

export function expectedSourceFromRetrievalResult(result) {
  return compactExpectedSource({
    document_id: result?.document_id,
    document_version_id: result?.document_version_id,
    chunk_id: result?.chunk_id,
    required: true,
  });
}

export function expectedSourceFromDocumentChunk(documentId, chunk) {
  return compactExpectedSource({
    document_id: documentId,
    document_version_id: chunk?.document_version_id,
    chunk_id: chunk?.id,
    required: true,
  });
}

export function expectedSourceKey(source) {
  return [
    source?.document_id || "",
    source?.document_version_id || "",
    source?.chunk_id || "",
  ].join(":");
}

export function addExpectedSource(sources, source) {
  const current = Array.isArray(sources) ? [...sources] : [];
  if (!source?.document_id || !source?.chunk_id) {
    return current;
  }
  const key = expectedSourceKey(source);
  if (current.some((item) => expectedSourceKey(item) === key)) {
    return current;
  }
  return [...current, source];
}

export function removeExpectedSource(sources, index) {
  const current = Array.isArray(sources) ? [...sources] : [];
  current.splice(index, 1);
  return current;
}

export function expectedSourceSummary(source) {
  const chunk = source?.chunk_id ? `chunk ${shortId(source.chunk_id)}` : "документ";
  const version = source?.document_version_id ? ` · версия ${shortId(source.document_version_id)}` : "";
  return `${chunk}${version}`;
}

function compactExpectedSource(source) {
  return Object.fromEntries(
    Object.entries(source).filter(([_key, value]) => value !== undefined && value !== null && value !== ""),
  );
}

function shortId(value) {
  return String(value || "").slice(0, 8);
}

export function evaluationDatasetRunEndpoint(datasetId) {
  return `/api/evaluation/datasets/${encodeURIComponent(datasetId)}/runs`;
}

export function communitySmokeRequestBody(documentIds) {
  return JSON.stringify({
    document_ids: Array.isArray(documentIds) ? documentIds : [],
  });
}

export function externalRagasRunsEndpoint(runId) {
  return `/api/evaluation/runs/${encodeURIComponent(runId)}/external-ragas`;
}

export function externalRagasRunEndpoint(runId, externalRunId) {
  return `/api/evaluation/runs/${encodeURIComponent(runId)}/external-ragas/${encodeURIComponent(externalRunId)}`;
}

export function ragasExportFileName(run) {
  const dataset = slugPart(run?.dataset_name || "evaluation");
  const version = slugPart(run?.dataset_version || "run");
  const started = slugPart(String(run?.started_at || "").slice(0, 10)) || "date";
  return `freehub-ragas-${dataset}-${version}-${started}.json`;
}

export function externalRagasRunFileName(run) {
  const runner = slugPart(run?.runner || "external");
  const model = slugPart(run?.model || "ragas");
  const importedAt = slugPart(String(run?.source_imported_at || run?.created_at || "").slice(0, 10));
  const date = importedAt || "date";
  return `freehub-external-ragas-${runner}-${model}-${date}.json`;
}

export function filterExternalRagasRuns(runs, query) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) {
    return runs || [];
  }
  return (runs || []).filter((run) =>
    [run?.runner, run?.model].some((value) =>
      String(value || "").toLowerCase().includes(normalizedQuery),
    ),
  );
}

export function sortExternalRagasRuns(runs, sortKey) {
  const values = [...(runs || [])];
  if (sortKey === "date_asc") {
    return values.sort(
      (left, right) =>
        externalRagasRunTimestamp(left) - externalRagasRunTimestamp(right),
    );
  }
  if (String(sortKey || "").endsWith("_desc")) {
    const metric = String(sortKey).replace(/_desc$/, "");
    if (metric !== "date") {
      return values.sort(
        (left, right) =>
          externalRagasMetric(right, metric) - externalRagasMetric(left, metric),
      );
    }
  }
  return values.sort(
    (left, right) =>
      externalRagasRunTimestamp(right) - externalRagasRunTimestamp(left),
  );
}

export function selectedExternalRagasRun(runs, selectedId) {
  return (runs || []).find((run) => run?.id === selectedId) || null;
}

export function bestExternalRagasRun(runs, metric) {
  let bestRun = null;
  let bestValue = null;
  for (const run of runs || []) {
    const value = externalRagasMetric(run, metric);
    if (value === null || Number.isNaN(value)) {
      continue;
    }
    if (bestValue === null || value > bestValue) {
      bestRun = run;
      bestValue = value;
    }
  }
  return bestRun;
}

export function runAggregateMetric(run, key) {
  const value = run?.aggregate_metrics?.[key];
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return Number(value);
}

export function evaluationMetricDelta(baseRun, candidateRun, key) {
  const base = runAggregateMetric(baseRun, key);
  const candidate = runAggregateMetric(candidateRun, key);
  if (base === null || candidate === null || Number.isNaN(base) || Number.isNaN(candidate)) {
    return null;
  }
  return candidate - base;
}

export function evaluationMetricDeltaTone(delta) {
  if (delta === null || delta === undefined || Number.isNaN(delta) || Math.abs(delta) < 0.0005) {
    return "neutral";
  }
  return delta > 0 ? "positive" : "negative";
}

export function externalRagasMetric(run, key) {
  const value = run?.aggregate_metrics?.[key];
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return Number(value);
}

export function externalRagasMetricDelta(evaluationRun, externalRun, key) {
  const internal = runAggregateMetric(evaluationRun, key);
  const external = externalRagasMetric(externalRun, key);
  if (
    internal === null ||
    external === null ||
    Number.isNaN(internal) ||
    Number.isNaN(external)
  ) {
    return null;
  }
  return external - internal;
}

export function externalRagasRunMetricDelta(baseRun, candidateRun, key) {
  const base = externalRagasMetric(baseRun, key);
  const candidate = externalRagasMetric(candidateRun, key);
  if (
    base === null ||
    candidate === null ||
    Number.isNaN(base) ||
    Number.isNaN(candidate)
  ) {
    return null;
  }
  return candidate - base;
}

export function externalRagasCaseResult(externalRun, caseResult, index) {
  const caseResults = externalRun?.case_results;
  if (!Array.isArray(caseResults)) {
    return null;
  }
  const caseId = caseResult?.case_id;
  if (caseId) {
    const matched = caseResults.find((result) => result?.case_id === caseId);
    if (matched) {
      return matched;
    }
  }
  return caseResults[index] || null;
}

export function externalRagasCaseMetric(externalRun, caseResult, index, key) {
  const externalCaseResult = externalRagasCaseResult(externalRun, caseResult, index);
  const value = externalCaseResult?.[key];
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return Number(value);
}

export function externalRagasCaseMetricDelta(caseResult, externalRun, index, key) {
  const internal = answerMetric(caseResult, key);
  const external = externalRagasCaseMetric(externalRun, caseResult, index, key);
  if (
    internal === null ||
    external === null ||
    Number.isNaN(Number(internal)) ||
    Number.isNaN(external)
  ) {
    return null;
  }
  return external - Number(internal);
}

export function externalRagasRunUsesCaseIds(run) {
  const caseResults = run?.case_results;
  return (
    Array.isArray(caseResults) &&
    caseResults.length > 0 &&
    caseResults.every((caseResult) => String(caseResult?.case_id || "").trim())
  );
}

export function externalRagasRunJsonPayload(run) {
  return {
    id: run?.id || "",
    runner: run?.runner || "",
    model: run?.model || "",
    base_url: run?.base_url || null,
    source: {
      file_name: run?.source_file_name || null,
      imported_by: run?.source_imported_by || null,
      imported_at: run?.source_imported_at || null,
    },
    aggregate: run?.aggregate_metrics || {},
    cases: run?.case_results || [],
  };
}

export function normalizeExternalRagasImportPayload(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Файл должен содержать JSON-объект результата RAGAS");
  }
  const runner = String(value.runner || "").trim();
  const model = String(value.model || "").trim();
  const cases = value.cases;
  if (!runner || !model) {
    throw new Error("В RAGAS JSON должны быть поля runner и model");
  }
  if (!Array.isArray(cases)) {
    throw new Error("В RAGAS JSON поле cases должно быть массивом");
  }
  return {
    runner,
    model,
    base_url: String(value.base_url || "").trim() || null,
    source_file_name: String(value.source_file_name || "").trim() || null,
    source_imported_by: String(value.source_imported_by || "").trim() || "community",
    aggregate: value.aggregate && typeof value.aggregate === "object" ? value.aggregate : {},
    cases,
  };
}

function externalRagasRunTimestamp(run) {
  return new Date(run?.source_imported_at || run?.created_at || 0).getTime() || 0;
}

function slugPart(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/giu, "-")
    .replace(/^-+|-+$/g, "");
}
